import React, { useState } from 'react';
import { Check, Star, Zap, Shield, Award, Microscope, School, Rocket, Users, Building2, ArrowRight, HelpCircle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PopupExample from './WaitingListPopup';
import { toast, ToastContainer } from 'react-toastify';

const PricingCard = ({
    title,
    price,
    period,
    description,
    features,
    buttonText,
    isPopular,
    icon: Icon,
    onButtonClick,
    badge,
    bestFor,
    timeline
}) => (
    <div
        className={`relative flex flex-col h-full p-6 rounded-2xl transition-all duration-300 ${isPopular
            ? 'bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-2xl scale-105 z-10 border-2 border-blue-400'
            : 'bg-white text-gray-900 shadow-lg hover:shadow-2xl border-2 border-gray-100 hover:border-blue-200'
            }`}
    >
        {isPopular && (
            <div className="absolute top-0 right-0 -mt-3 -mr-3">
                <span className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide bg-gradient-to-r from-yellow-400 to-orange-400 text-blue-900 shadow-lg animate-pulse">
                    ⭐ {badge || 'Most Popular'}
                </span>
            </div>
        )}

        {badge && !isPopular && (
            <div className="absolute top-0 right-0 -mt-3 -mr-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide bg-green-100 text-green-700 shadow-md">
                    {badge}
                </span>
            </div>
        )}

        <div className="mb-5">
            <div className={`p-3 rounded-xl w-fit mb-4 ${isPopular ? 'bg-white/20' : 'bg-blue-50'}`}>
                <Icon className={`w-7 h-7 ${isPopular ? 'text-white' : 'text-blue-600'}`} />
            </div>
            <h3 className={`text-2xl font-bold mb-2 ${isPopular ? 'text-white' : 'text-blue-900'}`}>
                {title}
            </h3>
            <p className={`text-sm mb-4 min-h-[2.5rem] ${isPopular ? 'text-blue-100' : 'text-gray-600'}`}>
                {description}
            </p>

            <div className="flex items-baseline mb-3">
                <span className="text-4xl font-bold tracking-tight">
                    {typeof price === 'string' && price.includes('html') ? (
                        <span dangerouslySetInnerHTML={{ __html: price }} />
                    ) : (
                        price
                    )}
                </span>
                {period && (
                    <span className={`ml-2 text-sm font-medium ${isPopular ? 'text-blue-200' : 'text-gray-500'}`}>
                        /{period}
                    </span>
                )}
            </div>

            {bestFor && (
                <div className={`mb-4 p-3 rounded-lg ${isPopular ? 'bg-white/10' : 'bg-gray-50'}`}>
                    <div className="text-xs">
                        <span className={`font-bold uppercase ${isPopular ? 'text-blue-200' : 'text-gray-500'}`}>
                            Best For:{' '}
                        </span>
                        <span className={`font-medium ${isPopular ? 'text-white' : 'text-gray-700'}`}>
                            {bestFor}
                        </span>
                    </div>
                </div>
            )}

            {timeline && (
                <div className={`text-xs font-semibold ${isPopular ? 'text-blue-200' : 'text-blue-600'}`}>
                    ⏱️ Delivery: {timeline}
                </div>
            )}
        </div>

        <div className="flex-grow">
            <div className={`w-full h-px mb-5 ${isPopular ? 'bg-blue-400' : 'bg-gray-200'}`} />
            <ul className="space-y-3 mb-6">
                {features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                        <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isPopular ? 'text-yellow-300' : 'text-green-600'}`} />
                        <span className={`text-sm leading-relaxed ${isPopular ? 'text-blue-50' : 'text-gray-700'}`}>
                            {feature}
                        </span>
                    </li>
                ))}
            </ul>
        </div>

        <button
            onClick={onButtonClick}
            className={`w-full py-3.5 px-6 rounded-xl font-bold text-sm transition-all duration-200 transform hover:-translate-y-1 hover:shadow-xl ${isPopular
                ? 'bg-white text-blue-600 hover:bg-blue-50 shadow-lg'
                : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-md'
                }`}
        >
            {buttonText}
        </button>
    </div>
);

const TabButton = ({ active, onClick, children, icon: Icon }) => (
    <button
        onClick={onClick}
        className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 ${active
            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg scale-105'
            : 'bg-white text-gray-600 hover:bg-gray-50 shadow-md hover:shadow-lg'
            }`}
    >
        {Icon && <Icon className="w-5 h-5" />}
        {children}
    </button>
);

const PricingPageComplete = () => {
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [pro, setPro] = useState('');
    const [activeTab, setActiveTab] = useState('startups'); // 'startups', 'students', 'enterprise'
    const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'yearly'

    const handleContactSales = (planName) => {
        setPro(planName);
        setIsOpen(true);
    };

    // Startup Self-Serve Tiers
    const startupSelfServe = [
        {
            title: 'Free Starter',
            price: '$0',
            period: 'month',
            description: 'Perfect for hobbyists exploring synthetic data.',
            icon: Star,
            features: [
                '500 rows per generation',
                'Max 10,000 data points/month',
                '3 generations per day',
                'Text-based data only',
                'Community templates',
                'CSV/JSON export',
                'Community support'
            ],
            buttonText: 'Start for Free',
            onButtonClick: () => navigate('/signin'),
            isPopular: false,
            bestFor: 'Individual developers, early exploration'
        },
        {
            title: 'Pro Creator',
            price: billingCycle === 'monthly' ? '$15' : '<span class="line-through text-gray-400">$15</span> $12',
            period: 'month',
            description: 'For data scientists and production apps.',
            icon: Zap,
            features: [
                'Everything in Free',
                '1000 rows per generation',
                'Max 200,000 data points/month',
                'Unlimited generations',
                'Web search integration',
                'File upload (10MB)',
                'Advanced templates',
                'API access (10K calls/month)',
                'Custom Dataset Formats',
                'Email support (48hr)'
            ],
            buttonText: 'Join Waiting List',
            onButtonClick: () => handleContactSales('Pro Creator'),
            isPopular: true,
            badge: billingCycle === 'yearly' ? 'Save 20%' : 'Best Value',
            bestFor: 'Solo founders, indie developers'
        },
        {
            title: 'Team',
            price: billingCycle === 'monthly' ? '$49' : '<span class="line-through text-gray-400">$49</span> $39',
            period: 'month',
            description: 'Scalable solutions for growing startups.',
            icon: Users,
            features: [
                'Everything in Pro',
                '5,000 rows per generation',
                'Max 1,000,000 data points/month',
                'Multi-modal support (beta)',
                'File upload (100MB)',
                'Fine-tuning assistance',
                'Team collaboration (10 seats)',
                'API access (100K calls/month)',
                'Priority chat support (24hr)'
            ],
            buttonText: 'Contact Sales',
            onButtonClick: () => handleContactSales('Team'),
            isPopular: false,
            badge: billingCycle === 'yearly' ? 'Save 20%' : '',
            bestFor: 'Growing startups, funded companies'
        }
    ];


    // Startup Managed Services
    const startupManaged = [
        {
            title: 'Dedicated Service',
            price: '$200+',
            period: 'one-time',
            description: 'Complete done-for-you datasets. Ecosystem partners get 20% off.',
            icon: Shield,
            features: [
                '10,000-150,000 rows custom dataset',
                'We handle everything (prompting, generation, cleaning)',
                'Dedicated team assigned to your project',
                'Domain expert consultation (2-5 hours)',
                'Expert human review',
                'Advanced cleaning & bias mitigation',
                'Data quality report',
                'Fine-tuning and evaluation guidance',
                'Ready-to-train format',
                'Private server deployment option (server price excluded)',
                'Comprehensive documentation',
                '1 month of revisions'
            ],
            buttonText: 'Contact Sales',
            onButtonClick: () => handleContactSales('Dedicated Service Package'),
            isPopular: true,
            badge: 'Done For You',
            bestFor: 'Startups needing production datasets',
            timeline: '5-14 business days'
        },
        {
            title: 'Ecosystem Partner Benefits',
            price: '20% Off',
            period: 'all services',
            description: 'Special pricing for accelerator/incubator portfolio companies.',
            icon: Rocket,
            features: [
                '💰 20% discount on all self-serve plans',
                '💰 20% discount on managed services',
                '🎓 2 Free technical training sessions',
                '📊 All features from Dedicated Service',
                '🚀 Priority support',
                '📈 Quarterly business reviews',
                '🤝 Co-marketing opportunities',
                '🔧 Private server deployment guidance'
            ],
            buttonText: 'Partner Inquiry',
            onButtonClick: () => handleContactSales('Ecosystem Partner Program'),
            isPopular: false,
            badge: 'Save 20%',
            bestFor: 'Accelerator/incubator portfolio companies',
            timeline: 'Ongoing partnership'
        }
    ];

    // Student Tiers
    const studentTiers = [
        {
            title: 'Student Basic',
            price: 'Free',
            description: 'Practice & explore synthetic data.',
            icon: School,
            features: [
                '500 rows per generation',
                'Max 10000 data points/month',
                '3 generations per day',
                'Ready-made templates',
                'Community datasets',
                'CSV export',
                'Community support'
            ],
            buttonText: 'Start for Free',
            onButtonClick: () => navigate('/signin'),
            isPopular: false,
            bestFor: 'Undergrad students, learners'
        },
        {
            title: 'Student Plus',
            price: '$5',
            period: 'month',
            description: 'Build impressive projects that stand out.',
            icon: Star,
            features: [
                'Everything in Basic',
                '1000 rows per generation',
                'Max 25000 data points/month',
                'Unlimited generations',
                'Web search integration',
                'File upload (2MB)',
                'CSV/JSON/JSONL export',
                'Storage for 3 datasets'
            ],
            buttonText: 'Join Waiting List',
            onButtonClick: () => handleContactSales('Student Plus'),
            isPopular: false,
            bestFor: 'Serious students, hackathons'
        },
        {
            title: 'Custom Dataset',
            price: '$9',
            period: 'one-time',
            description: 'We create your project dataset for you.',
            icon: Zap,
            features: [
                'Upto 10000 rows custom dataset',
                'We handle prompting & generation',
                'Basic cleaning (duplicates, formatting)',
                'CSV/JSON export',
                'Ready-to-train format',
                'Basic documentation (1-page)'
            ],
            buttonText: 'Order Dataset',
            onButtonClick: () => handleContactSales('Custom Dataset Package'),
            isPopular: true,
            badge: 'Most Popular',
            bestFor: 'Final-year projects, internships',
            timeline: '48-72 hours'
        },
        {
            title: 'Certified Project',
            price: '$39',
            period: 'one-time',
            description: 'Validate your dataset & earn a certificate.',
            icon: Award,
            features: [
                'Everything in Custom Dataset',
                'Upto 25000 data points',
                'Additional 200/- per 10000 points after 25000',
                'Project Guidance from experienced AI Engineers',
                'Dataset quality evaluation by domain experts',
                'Verifiable certificate with QR code',
                'LinkedIn shareable badge',
                'Student showcase feature (optional)'
            ],
            buttonText: 'Get Certified',
            onButtonClick: () => handleContactSales('Certified Project Package'),
            isPopular: false,
            badge: 'For Resumes',
            bestFor: 'Placements, higher education abroad',
            timeline: '24-48 hours'
        }
    ];

    // Enterprise Tier
    const enterpriseTier = [
        {
            title: 'Enterprise Custom',
            price: '$599+',
            period: 'month',
            description: 'Unlimited scale with dedicated support.',
            icon: Building2,
            features: [
                'Unlimited data points & generations',
                'Multi-modal support (text, voice, image)',
                'Dedicated account manager',
                'Custom SLA (99.9% uptime)',
                'On-premise deployment option',
                'Advanced security (SOC 2, GDPR)',
                'Unlimited API calls',
                'Personalized expert review',
                'Unlimited revisions',
                'Custom integrations',
                'Onboarding & training (20 users)',
                'Multi-language support',
                'Priority feature requests'
            ],
            buttonText: 'Contact Sales',
            onButtonClick: () => handleContactSales('Enterprise'),
            isPopular: true,
            badge: 'Custom Solutions',
            bestFor: 'Large companies, 50+ employees'
        }
    ];

    const getCurrentPlans = () => {
        if (activeTab === 'startups') {
            return {
                title: 'Startup & Developer Pricing',
                subtitle: 'Production-ready datasets without the hassle',
                selfServe: startupSelfServe,
                managed: startupManaged
            };
        } else if (activeTab === 'students') {
            return {
                title: 'Student Pricing',
                subtitle: 'Build impressive projects faster',
                selfServe: studentTiers.slice(0, 2),
                managed: studentTiers.slice(2)
            };
        } else {
            return {
                title: 'Enterprise Solutions',
                subtitle: 'Scale AI development with confidence',
                selfServe: [],
                managed: enterpriseTier
            };
        }
    };

    const currentPlans = getCurrentPlans();

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
            <PopupExample hidden={isOpen} isOpen={isOpen} setIsOpen={setIsOpen} pro={pro} toast={toast} />

            {/* Hero Section */}
            <section className="pt-12 pb-6 px-4">
                <div className="max-w-7xl mx-auto text-center">
                    <h1 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2">
                        Simple, Transparent Pricing
                    </h1>
                    <p className="text-base text-gray-600 max-w-3xl mx-auto mb-5">
                        Choose the perfect plan for your needs. From students to enterprises, we've got you covered.
                    </p>

                    {/* Tab Navigation */}
                    <div className="flex flex-wrap justify-center gap-2 mb-6">
                        <TabButton
                            active={activeTab === 'startups'}
                            onClick={() => setActiveTab('startups')}
                            icon={Rocket}
                        >
                            Startups & Engineers
                        </TabButton>
                        <TabButton
                            active={activeTab === 'students'}
                            onClick={() => setActiveTab('students')}
                            icon={School}
                        >
                            Students
                        </TabButton>
                        <TabButton
                            active={activeTab === 'enterprise'}
                            onClick={() => setActiveTab('enterprise')}
                            icon={Building2}
                        >
                            Enterprise
                        </TabButton>
                    </div>

                    {/* Billing Toggle (only for startups self-serve) */}
                    {activeTab === 'startups' && (
                        <div className="flex items-center justify-center gap-3 mb-4">
                            <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-500'}`}>
                                Monthly
                            </span>
                            <button
                                onClick={() => setBillingCycle(prev => prev === 'monthly' ? 'yearly' : 'monthly')}
                                className="relative w-14 h-8 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-lg"
                            >
                                <div
                                    className={`absolute top-1 left-1 bg-white w-6 h-6 rounded-full shadow-md transform transition-transform duration-200 ${billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-0'
                                        }`}
                                />
                            </button>
                            <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-gray-900' : 'text-gray-500'}`}>
                                Yearly <span className="text-green-600 font-bold text-xs ml-1">💰 Save 20%</span>
                            </span>
                        </div>
                    )}
                </div>
            </section>

            {/* Pricing Cards Section */}
            <section className="pb-10 px-4">
                <div className="max-w-7xl mx-auto">
                    {/* Self-Serve Plans */}
                    {currentPlans.selfServe.length > 0 && (
                        <>
                            <div className="text-center mb-6">
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                    {activeTab === 'students' ? 'Self-Serve Plans' : 'Self-Serve Tiers'}
                                </h2>
                                <p className="text-gray-600 text-sm">
                                    {activeTab === 'students'
                                        ? 'Build datasets yourself with our tools'
                                        : 'Build production-ready datasets yourself'}
                                </p>
                            </div>
                            <div className={`grid ${currentPlans.selfServe.length === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'} gap-6 mb-12`}>
                                {currentPlans.selfServe.map((plan, index) => (
                                    <div key={index} className="h-full">
                                        <PricingCard {...plan} />
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {/* Managed Services */}
                    {currentPlans.managed.length > 0 && (
                        <>
                            <div className="text-center mb-6 mt-10">
                                <div className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 font-semibold text-sm mb-3">
                                    ✨ Done-For-You Service
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                    {activeTab === 'students' ? 'Managed Services' : activeTab === 'enterprise' ? 'Enterprise Solutions' : 'Dedicated Service'}
                                </h2>
                                <p className="text-gray-600 text-sm">
                                    {activeTab === 'students'
                                        ? 'We create your dataset for you'
                                        : activeTab === 'enterprise'
                                            ? 'Custom solutions for large organizations'
                                            : 'We handle everything - you get production-ready data'}
                                </p>
                            </div>
                            <div className={`grid ${currentPlans.managed.length === 1 ? 'md:grid-cols-1 max-w-2xl mx-auto' : currentPlans.managed.length === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2'} gap-6`}>
                                {currentPlans.managed.map((plan, index) => (
                                    <div key={index} className="h-full">
                                        <PricingCard {...plan} />
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-10 px-4 bg-white">
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-2xl font-bold text-center text-gray-900 mb-6">
                        Frequently Asked Questions
                    </h2>
                    <div className="space-y-4">
                        <details className="group bg-gray-50 rounded-xl p-5 hover:bg-gray-100 transition-colors">
                            <summary className="flex items-center justify-between cursor-pointer font-semibold text-gray-900">
                                <span>What does "Certified Project" mean?</span>
                                <HelpCircle className="w-5 h-5 text-blue-600 group-open:rotate-180 transition-transform" />
                            </summary>
                            <p className="mt-4 text-gray-600 leading-relaxed">
                                The <strong>Certified Project package (₹1,999)</strong> is designed for students who want to stand out in placements and university applications. We don't just create your dataset—we provide <strong>expert guidance from experienced AI engineers</strong> throughout your project. Your dataset (up to 25,000 data points) is <strong>evaluated by domain experts</strong> who assess quality, balance, and real-world applicability. You receive a <strong>verifiable certificate with a QR code</strong>, a detailed quality report, and a <strong>LinkedIn shareable badge</strong>. This credential demonstrates that your project meets industry standards and has been validated by professionals, making it highly valuable for higher education abroad and job placements.
                            </p>
                        </details>

                        <details className="group bg-gray-50 rounded-xl p-5 hover:bg-gray-100 transition-colors">
                            <summary className="flex items-center justify-between cursor-pointer font-semibold text-gray-900">
                                <span>What's the difference between Custom Dataset and Certified Project?</span>
                                <HelpCircle className="w-5 h-5 text-blue-600 group-open:rotate-180 transition-transform" />
                            </summary>
                            <p className="mt-4 text-gray-600 leading-relaxed">
                                <strong>Custom Dataset (₹699):</strong> We create a basic dataset (up to 10,000 rows) for your project. Includes prompting, generation, basic cleaning, and documentation. Delivered in 48-72 hours. Perfect for college projects and internships.<br /><br />
                                <strong>Certified Project (₹1,999):</strong> Everything in Custom Dataset PLUS expert guidance from AI engineers, domain expert evaluation, verifiable certificate, quality report, and LinkedIn badge. Up to 25,000 data points. Ideal for placements and higher education applications where you need to demonstrate professional-level work.
                            </p>
                        </details>

                        <details className="group bg-gray-50 rounded-xl p-5 hover:bg-gray-100 transition-colors">
                            <summary className="flex items-center justify-between cursor-pointer font-semibold text-gray-900">
                                <span>What are Ecosystem Partner benefits?</span>
                                <HelpCircle className="w-5 h-5 text-blue-600 group-open:rotate-180 transition-transform" />
                            </summary>
                            <p className="mt-4 text-gray-600 leading-relaxed">
                                Accelerators, incubators, and VCs can partner with us to give their portfolio companies <strong>20% off all services</strong> (self-serve and managed). Partners also get 2 free technical training sessions, priority support, quarterly business reviews, co-marketing opportunities, and private server deployment guidance. Minimum requirement: 5 portfolio companies or ₹50,000 annual contract value.
                            </p>
                        </details>

                        <details className="group bg-gray-50 rounded-xl p-5 hover:bg-gray-100 transition-colors">
                            <summary className="flex items-center justify-between cursor-pointer font-semibold text-gray-900">
                                <span>Can I upgrade or downgrade my plan?</span>
                                <HelpCircle className="w-5 h-5 text-blue-600 group-open:rotate-180 transition-transform" />
                            </summary>
                            <p className="mt-4 text-gray-600 leading-relaxed">
                                Yes! You can upgrade or downgrade your subscription at any time. If you upgrade, you'll get immediate access to new features. If you downgrade, changes take effect at the start of your next billing cycle. For managed services, contact us to discuss your needs.
                            </p>
                        </details>

                        <details className="group bg-gray-50 rounded-xl p-5 hover:bg-gray-100 transition-colors">
                            <summary className="flex items-center justify-between cursor-pointer font-semibold text-gray-900">
                                <span>What is your revision and refund policy?</span>
                                <HelpCircle className="w-5 h-5 text-blue-600 group-open:rotate-180 transition-transform" />
                            </summary>
                            <p className="mt-4 text-gray-600 leading-relaxed">
                                For managed services (Custom Dataset, Certified Project, Dedicated Service), we offer <strong>unlimited revisions</strong> until you're satisfied with the dataset quality. If after multiple revisions the dataset still doesn't meet your requirements, we offer a <strong>50% partial refund</strong>. For subscriptions (Student Plus, Pro Creator, Team), we offer a <strong>7-day money-back guarantee</strong> on your first payment only.
                            </p>
                        </details>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-10 px-4 bg-gradient-to-r from-blue-600 to-indigo-700">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl font-bold text-white mb-4">
                        Ready to Get Started?
                    </h2>
                    <p className="text-lg text-blue-100 mb-6">
                        Join thousands of developers and students building better AI with synthetic data.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <button
                            onClick={() => navigate('/signin')}
                            className="px-8 py-4 bg-white text-blue-600 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all duration-200 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
                        >
                            Start for Free <ArrowRight className="inline w-5 h-5 ml-2" />
                        </button>
                        <button
                            onClick={() => handleContactSales('General Inquiry')}
                            className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-xl font-bold text-lg hover:bg-white hover:text-blue-600 transition-all duration-200 shadow-xl"
                        >
                            Contact Sales
                        </button>
                    </div>
                </div>
            </section>

            <ToastContainer position="top-center" autoClose={3000} />
        </div>
    );
};

export default PricingPageComplete;
